import {
  normalizeCardImages,
  type CardIdentity,
  type CardRecord,
  type KitDetail,
  type KitImage,
  type KitItemAddOutcome,
  type KitStitchingJob,
  type KitSummary,
} from '../shared/types';
import { KIT_SUPPORTED_CARD_COUNTS, selectKitStitchingPattern } from '../shared/types';

export function summarizeKitImages(images: readonly KitImage[]): string {
  if (images.length === 0) return 'No images';
  const current = images.filter((image) => image.status === 'CURRENT').length;
  const stale = images.length - current;
  return [current > 0 ? `${current} current` : undefined, stale > 0 ? `${stale} stale` : undefined]
    .filter((part): part is string => part !== undefined)
    .join(', ');
}

export function summarizeKitJobs(jobs: readonly KitStitchingJob[]): string {
  if (jobs.length === 0) return 'No jobs';
  const latest = [...jobs].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0]!;
  return `${latest.status} - ${jobs.length} total`;
}

export function summarizeKitListOutput(kit: KitSummary): string {
  if (kit.currentJobId === undefined) return 'No output';
  return kit.currentPatternId === undefined ? 'Job queued' : kit.currentPatternId;
}

export function kitCardIdentityLabel(card: CardRecord): string {
  if (card.identityStatus === 'IDENTIFIED' && card.identity !== undefined) {
    return identityLabel(card.identity);
  }
  if (card.csvSuggestion !== undefined) return `CSV: ${identityLabel(card.csvSuggestion)}`;
  const suggestion = card.suggestion?.identification;
  if (suggestion?.status === 'known') return `Suggested: ${identityLabel(suggestion.identity)}`;
  return 'Unidentified';
}

export function kitCardAddHint(
  card: CardRecord,
  selectedCardIds: ReadonlySet<string>,
): { status: 'selected' | 'blocked' | 'ready'; label: string } {
  if (selectedCardIds.has(card.cardId)) {
    return { status: 'selected', label: 'Already selected' };
  }
  if (card.imageQualityStatus !== 'ACCEPTED') {
    return {
      status: 'blocked',
      label:
        card.imageQualityStatus === undefined
          ? 'Image QA required'
          : `Image QA: ${card.imageQualityStatus}`,
    };
  }
  if (normalizeCardImages(card).front === undefined) {
    return { status: 'blocked', label: 'Missing front image' };
  }
  return { status: 'ready', label: 'Ready to add' };
}

export function kitAddOutcomeLabel(outcome: KitItemAddOutcome): string {
  switch (outcome.status) {
    case 'added':
      return 'Added';
    case 'already-in-kit':
      return 'Already selected';
    case 'image-qa-required':
      return 'Image QA must be accepted';
    case 'missing-front-image':
      return 'Missing front image';
    case 'not-found':
      return 'Card not found';
    case 'pulled':
      return 'Card is pulled';
    case 'marketplace-committed':
      return 'Card is committed to an individual marketplace listing';
    case 'reserved':
      return 'Card is reserved by another Kit';
    case 'invalid':
      return 'Invalid card';
  }
}

export function moveKitCardId(
  cardIds: readonly string[],
  cardId: string,
  direction: 'up' | 'down',
): readonly string[] {
  const currentIndex = cardIds.indexOf(cardId);
  if (currentIndex === -1) return cardIds;
  const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
  if (nextIndex < 0 || nextIndex >= cardIds.length) return cardIds;
  const next = [...cardIds];
  [next[currentIndex], next[nextIndex]] = [next[nextIndex]!, next[currentIndex]!];
  return next;
}

export interface KitReviewReadiness {
  ready: boolean;
  patternId?: string;
  messages: readonly string[];
}

export function getKitReviewReadiness(kit: KitDetail): KitReviewReadiness {
  const messages: string[] = [];
  const pattern = selectKitStitchingPattern(kit.itemCount);
  if (pattern === undefined) {
    messages.push(
      `Select exactly ${KIT_SUPPORTED_CARD_COUNTS.join(' or ')} cards. Current count: ${
        kit.itemCount
      }.`,
    );
  }
  const imageQaBlocks = kit.items.filter((item) => item.card.imageQualityStatus !== 'ACCEPTED');
  if (imageQaBlocks.length > 0) {
    messages.push(`${imageQaBlocks.length} selected card(s) need accepted Image QA.`);
  }
  const missingFront = kit.items.filter((item) => item.frontImageKey.trim() === '');
  if (missingFront.length > 0) {
    messages.push(`${missingFront.length} selected card(s) are missing a front image.`);
  }
  return {
    ready: messages.length === 0,
    ...(pattern !== undefined ? { patternId: pattern.patternId } : {}),
    messages,
  };
}

export function splitKitImages(images: readonly KitImage[]): {
  current: readonly KitImage[];
  stale: readonly KitImage[];
} {
  return {
    current: images.filter((image) => image.status === 'CURRENT'),
    stale: images.filter((image) => image.status !== 'CURRENT'),
  };
}

export function retryableKitJobs(jobs: readonly KitStitchingJob[]): readonly KitStitchingJob[] {
  return jobs.filter((job) => job.status === 'FAILED');
}

function identityLabel(identity: CardIdentity): string {
  return [identity.name, identity.number !== '' ? `#${identity.number}` : undefined, identity.set]
    .filter((part): part is string => part !== undefined && part !== '')
    .join(' - ');
}
